/**
 * Access denied (PRD §8.3).
 *
 * The user stays on auth.mindpeer.com and is told, in plain language, why the
 * module would not open. The server sends a machine-readable
 * `AccessDenialReason`; the copy lives here so modules never have to carry it.
 */
import { Link, useSearchParams } from 'react-router-dom';
import { ACCESS_DENIAL_REASON, type AccessDenialReason } from '@mindpeer/auth-shared';
import { AuthLayout } from '@/components/auth-layout';
import { DarkAlert, DarkButton, DarkSubmit } from '@/components/ui';
import { useAuth } from '@/hooks/use-auth';

/** What the user should do next, keyed by why they were refused. */
type Remedy = 'contact-org-admin' | 'contact-support' | 'verify-email' | 'wait-for-approval' | 'none';

interface Explanation {
  title: string;
  body: (module: string) => string;
  remedy: Remedy;
}

const EXPLANATIONS: Record<AccessDenialReason, Explanation> = {
  user_suspended: {
    title: 'Your account has been suspended',
    body: () =>
      'Your MindPeer account is currently suspended, so it cannot be used to open any module.',
    remedy: 'contact-support',
  },
  email_unverified: {
    title: 'Verify your email first',
    body: () => 'Your email address has not been verified yet, so module access is on hold.',
    remedy: 'verify-email',
  },
  organization_inactive: {
    title: 'Your organization is inactive',
    body: () =>
      "Your organization's MindPeer access has been suspended, which affects every module.",
    remedy: 'contact-org-admin',
  },
  membership_inactive: {
    title: 'Your membership is not active',
    body: () => 'Your membership of this organization is suspended or has been revoked.',
    remedy: 'contact-org-admin',
  },
  no_membership: {
    title: 'No organization yet',
    body: () => 'Your account is not a member of an organization, so no module can be opened yet.',
    remedy: 'wait-for-approval',
  },
  awaiting_organization_approval: {
    title: 'Your account is awaiting organization approval',
    body: () =>
      'A MindPeer administrator still needs to assign your account to an organization.',
    remedy: 'wait-for-approval',
  },
  application_unknown: {
    title: 'Unknown application',
    body: () => 'That application is not registered with MindPeer, so it cannot be opened.',
    remedy: 'contact-support',
  },
  application_inactive: {
    title: 'This module is unavailable',
    body: (module) => `${module} is not currently available. This is usually temporary.`,
    remedy: 'contact-support',
  },
  entitlement_missing: {
    title: 'Module not enabled',
    body: (module) => `${module} is not enabled for your organization.`,
    remedy: 'contact-org-admin',
  },
  entitlement_expired: {
    title: 'Access has expired',
    body: (module) => `Your organization's ${module} subscription or trial has expired.`,
    remedy: 'contact-org-admin',
  },
  entitlement_suspended: {
    title: 'Access is suspended',
    body: (module) => `Your organization's access to ${module} is currently suspended.`,
    remedy: 'contact-org-admin',
  },
  entitlement_not_started: {
    title: 'Access has not started yet',
    body: (module) =>
      `Your organization's ${module} access is scheduled to begin at a later date.`,
    remedy: 'contact-org-admin',
  },
  global_org_not_permitted: {
    title: 'Not available to Global accounts',
    body: (module) =>
      `${module} is only available to users in a business organization, and your account sits in the MindPeer Global organization.`,
    remedy: 'contact-support',
  },
  seat_limit_reached: {
    title: 'No seats available',
    body: (module) =>
      `Your organization has used every available ${module} seat. An administrator can free one up or add more.`,
    remedy: 'contact-org-admin',
  },
};

const FALLBACK: Explanation = {
  title: 'Access denied',
  body: (module) => `You do not currently have access to ${module}.`,
  remedy: 'contact-support',
};

function isKnownReason(value: string | null): value is AccessDenialReason {
  return value !== null && (ACCESS_DENIAL_REASON as readonly string[]).includes(value);
}

/**
 * Turn a client id into something presentable, e.g. `meridian` → `Meridian`.
 *
 * The id arrives in the query string, so it is stripped to a safe character set
 * and length before display: this page is reached by redirect, and must not
 * render whatever a crafted link puts in `application`.
 */
function moduleLabel(raw: string | null): string {
  if (!raw) return 'this module';
  const cleaned = raw.replace(/[^a-zA-Z0-9 _-]/g, '').trim().slice(0, 64);
  if (!cleaned) return 'this module';
  return cleaned
    .split(/[\s_-]+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function AccessDeniedPage() {
  const [params] = useSearchParams();
  const { user } = useAuth();

  const reasonParam = params.get('reason');
  const explanation = isKnownReason(reasonParam) ? EXPLANATIONS[reasonParam] : FALLBACK;
  const module = moduleLabel(params.get('application') ?? params.get('client_id'));

  return (
    <AuthLayout
      title={explanation.title}
      footer={
        user ? (
          <Link to="/account" className="font-medium text-brand-400 hover:text-brand-300">
            Go to your account
          </Link>
        ) : (
          <Link to="/sign-in" className="font-medium text-brand-400 hover:text-brand-300">
            Sign in with a different account
          </Link>
        )
      }
    >
      <div className="space-y-5">
        <DarkAlert tone="warning">{explanation.body(module)}</DarkAlert>

        <Remedy remedy={explanation.remedy} />

        {/* The reason code makes a support conversation much faster. */}
        {isKnownReason(reasonParam) && (
          <p className="text-xs text-slate-500">Reference: {reasonParam}</p>
        )}
      </div>
    </AuthLayout>
  );
}

function Remedy({ remedy }: { remedy: Remedy }) {
  switch (remedy) {
    case 'verify-email':
      return (
        <DarkSubmit type="button" onClick={() => window.location.assign('/verify-email')}>
          Verify your email
        </DarkSubmit>
      );
    case 'wait-for-approval':
      return (
        <>
          <p className="text-sm text-slate-400">
            You&apos;ll be able to continue as soon as your organization is assigned.
          </p>
          <DarkButton onClick={() => window.location.assign('/awaiting-approval')}>
            What happens next?
          </DarkButton>
        </>
      );
    case 'contact-org-admin':
      return (
        <p className="text-sm text-slate-400">
          Your organization&apos;s MindPeer administrator can enable this. If you&apos;re not sure
          who that is, MindPeer support can point you to them.
        </p>
      );
    case 'contact-support':
      return (
        <p className="text-sm text-slate-400">
          If you think this is a mistake, contact MindPeer support and quote the reference below.
        </p>
      );
    case 'none':
      return null;
  }
}
